/**
 * Cognito JWT token utilities
 * Handles parsing, decoding, and extracting user info from Cognito tokens
 */

export interface CognitoIdToken {
  sub: string;
  email: string;
  email_verified: boolean;
  username: string;
  "cognito:username": string;
  "cognito:groups"?: string[];
  iss: string;
  aud: string;
  auth_time: number;
  exp: number;
  iat: number;
}

export interface CognitoAccessToken {
  sub: string;
  "cognito:groups"?: string[];
  token_use: "access";
  scope: string;
  auth_time: number;
  exp: number;
  iat: number;
  client_id: string;
  username: string;
}

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
}

/**
 * Decode JWT token (no signature verification - only use for extracting claims)
 */
export function decodeJWT<T = any>(token: string): T | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error("Invalid JWT format");
      return null;
    }

    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return decoded as T;
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
}

/**
 * Check if JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT<{ exp: number }>(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return decoded.exp < now;
}

/**
 * Store tokens in localStorage
 */
export function storeTokens(tokens: CognitoTokens): void {
  localStorage.setItem("cognito_id_token", tokens.idToken);
  localStorage.setItem("cognito_access_token", tokens.accessToken);

  if (tokens.refreshToken) {
    localStorage.setItem("cognito_refresh_token", tokens.refreshToken);
  }

  if (tokens.expiresIn) {
    const expiresAt = Date.now() + tokens.expiresIn * 1000;
    localStorage.setItem("cognito_expires_at", expiresAt.toString());
  }
}

/**
 * Retrieve tokens from localStorage
 */
export function getStoredTokens(): CognitoTokens | null {
  const idToken = localStorage.getItem("cognito_id_token");
  const accessToken = localStorage.getItem("cognito_access_token");
  const refreshToken = localStorage.getItem("cognito_refresh_token");

  if (!accessToken) {
    return null;
  }

  return {
    idToken: idToken || "",
    accessToken,
    refreshToken: refreshToken || undefined,
  };
}

/**
 * Clear tokens from localStorage
 */
export function clearStoredTokens(): void {
  localStorage.removeItem("cognito_id_token");
  localStorage.removeItem("cognito_access_token");
  localStorage.removeItem("cognito_refresh_token");
  localStorage.removeItem("cognito_expires_at");
}

/**
 * Check if tokens are valid (exist and not expired)
 */
export function hasValidTokens(): boolean {
  const tokens = getStoredTokens();
  if (!tokens || !tokens.accessToken) {
    return false;
  }

  return !isTokenExpired(tokens.accessToken);
}

/**
 * Get the time (in milliseconds) until the access token expires
 * Returns null if token is invalid or already expired
 */
export function getTimeUntilExpiry(): number | null {
  const tokens = getStoredTokens();
  if (!tokens || !tokens.accessToken) {
    return null;
  }

  const decoded = decodeJWT<{ exp: number }>(tokens.accessToken);
  if (!decoded || !decoded.exp) {
    return null;
  }

  const expiresAt = decoded.exp * 1000;
  const now = Date.now();
  const timeRemaining = expiresAt - now;

  return timeRemaining > 0 ? timeRemaining : null;
}
